import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import TimesheetFilterBar from "../components/TimesheetRevamp/TimesheetFilterBar";
import ResizeableTable from "./ResizeableTable";
import TabSection from "./TabSection";
import TimeSheetSideBar from "../TimeSheetSidebar";
import TimesheetKpiStrip from "../components/TimesheetRevamp/TimesheetKpiStrip";
import TimesheetBulkToolbar from "../components/TimesheetRevamp/TimesheetBulkToolbar";
import {
  BulkAnalyseProvider,
  BulkAnalyseStatusBar,
  BulkAnalyseToolbarButton,
} from "../bulkAnalyse";
import TimesheetResizeTray from "../components/TimesheetRevamp/TimesheetResizeTray";
import TimesheetUnapproveConfirmModal, {
  buildUnapproveTimesheetDetails,
} from "../components/TimesheetRevamp/TimesheetUnapproveConfirmModal";
import TimesheetApproveConfirmModal from "../components/TimesheetRevamp/TimesheetApproveConfirmModal";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { capitalizeAllLetters, formatHours } from "Utils/utils";
import {
  getTimesheetsOfWorkersAction,
  resetSingleCheckboxSelectedInTimesheet,
  singleCheckboxSelectedInTimesheet,
  singleSelectedTimesheetOfWorkerAction,
  singleSelectedTimesheetOfWorkerCall,
} from "store/actions/rostering.actions";
import PaginationComponent from "components/common/Pagination/index";
import {
  getSelectedTimesheetDateRange,
  getSingleCheckboxCheckedTimesheet,
  getTimesheetOfWorkersRequest,
  getSingleSelectedTimesheetDetailsLoader,
  getSingleSelectedTimesheetWorkerDetails,
} from "store/selector/rostering.selector";
import {
  emptyPaginationObj,
  NEW_TIMESHEET_STATUS,
  WORKER_TIMESHEETS_LIMIT,
  WORKER_SIDEBAR_LIMIT,
  getParticipantLabel,
} from "Constants/constant";
import useCareLabels from "hooks/useCareLabels";
import TimesheetPageSkeleton, {
  TimesheetTableBodySkeleton,
  TimesheetTableSkeleton,
  TimesheetWorkerSidebarSkeleton,
} from "../components/TimesheetRevamp/TimesheetPageSkeleton";
import moment from "moment";
import { toast } from "react-toastify";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  URL_ROSTERING_TIMESHEET_KPI_SUMMARY,
  URL_ROSTERING_TIMESHEET_STATUS_UPDATE,
  URL_ROSTERING_TIMESHEET_UNAPPROVE_FUNDING_ASSESSMENT,
  URL_ROSTERING_TIMESHEET_BULK_UNAPPROVE,
  URL_SP_AI_WALLET,
} from "store/services/URL";
import swal from "sweetalert";
import { addMonths } from "Utils/utils";
import { aiApi } from "store/services/ai.services";
import {
  TimesheetAiConfirmModal,
  TimesheetAiProgressModal,
  TimesheetAiResultsPanel,
  TimesheetAiReportDrawer,
  useTimesheetAiAccess,
  INITIAL_AI_PROGRESS_STATE,
  applyTimesheetAiProgressEvent,
  buildAiStatusFromBatchItem,
  buildTimesheetRowLabel,
  mergeBatchResultsIntoAiStatus,
  resolveTimesheetAiErrorMessage,
} from "./ai";
import {
  AI_PROGRESS_STREAM_CAP,
  runAiProgressFinishSequence,
} from "./ai/utils/useAiProgressDisplay";
import {
  releaseBodyScrollLock,
  releaseBodyScrollLockAfterTransition,
} from "./ai/utils/bodyScrollLock";
import {
  AI_DRAWER_SCROLL_DELAY_MS,
  scrollDetailPanelForAiNavigation,
  scrollDetailPanelIntoViewIfNeeded,
} from "./ai/utils/detailPanelScroll";
import {
  assessmentRequiresFundingWarning,
  BULK_UNAPPROVE_MAX,
  buildBulkUnapproveModalRows,
  countAmbiguousLinesFromAssessment,
  getEligibleUnapproveUuids,
  getSelectableStatusForFilter,
  isUnapproveBulkStatus,
} from "../utils/timesheetBulkUnapprove";
import {
  isAllParticipantsSelection,
  participantFilterKey,
  resolveTimesheetPtIdFilter,
} from "../utils/timesheetParticipantFilter";
import {
  parseTimesheetDeepLink,
  sameTimesheetWorkerId,
} from "../utils/timesheetDeepLink";

const parseHoursToMinutes = (val) => {
  if (val == null || val === "") return 0;
  const str = String(val);
  if (str.includes(":")) {
    const [h, m] = str.split(":").map((n) => parseInt(n, 10) || 0);
    return h * 60 + m;
  }
  const num = parseFloat(str);
  return Number.isFinite(num) ? num : 0;
};

/** Totals from loaded worker sidebar rows (all statuses, not status-filtered). */
const sumSidebarWorkerTotals = (workers) => {
  let timesheetCount = 0;
  let totalMinutes = 0;
  for (const w of workers) {
    timesheetCount += Number(w?.time_sheet_count) || 0;
    totalMinutes += parseHoursToMinutes(w?.total_hours);
  }
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  return {
    timesheetCount,
    hoursDisplay: formatHours(`${h}:${m}`) || "00:00",
  };
};


/** After approve/reject/unapprove, clamp page when the current page is empty or out of range. */
const resolveTimesheetPageAfterRefresh = (currentPage, timesheets) => {
  const rows = timesheets?.data ?? [];
  const total = Number(timesheets?.total) || 0;
  const totalPages = Math.max(1, Number(timesheets?.total_pages) || 1);
  const pageNum = Number(currentPage) || 1;

  if (total <= 0) {
    return 1;
  }
  if (pageNum > totalPages) {
    return totalPages;
  }
  if (rows.length === 0 && pageNum > 1) {
    return 1;
  }
  return pageNum;
};

const TimesheetDetails = ({
  selectedTimesheetDateRange,
  resetDateRange,
  singleSelectedWorkerTimesheets,
  selectedWorkerInSidebar,
  spRosteringSetting,
  spRosteringSettingLoader,
  setPage,
  page,
  sidebarLoadedCount,
  selectedParticipants,
  setSelectedParticipants,
  setCheckboxes,
  checkboxes,
  participantList,
  getParticipantList,
  getTimesheetsofWorkers,
  timeSheetsListOfWorkers,
  setSelectedWorkerInSidebar,
  workersLoader,
  onSidebarLoadedCountChange,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { participantLabelPlural } = useCareLabels();

  const dateRange = useSelector(getSelectedTimesheetDateRange);
  const lastWorkersQuery = useSelector(getTimesheetOfWorkersRequest);
  const timesheetDetailsList = useSelector(
    getSingleSelectedTimesheetWorkerDetails
  );

  const tableTimesheetsLoader = useSelector(
    getSingleSelectedTimesheetDetailsLoader
  );
  const viewedTimesheet = useSelector(getSingleCheckboxCheckedTimesheet);
  const tabSectionRef = useRef(null);
  const scrollToTabsAfterAiRef = useRef(false);
  const lastAppliedRangeRef = useRef({ from: null, to: null });
  const deepLinkRef = useRef(parseTimesheetDeepLink(location.search));
  const skipDefaultRangeRef = useRef(Boolean(deepLinkRef.current?.date));
  const deepLinkRowSelectedRef = useRef(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedOption, setSelectedOptions] = useState({
    label: "All",
    value: "all",
  });
  const [updatedAction, setUpdatedAction] = useState("pending");
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(0);
  const workspaceRef = useRef(null);
  const hasAutoSizedWorkspaceRef = useRef(false);

  const [paginationObj, setPaginationObj] = useState(emptyPaginationObj);

  const WORKSPACE_HEIGHT_MIN = 280;
  const WORKSPACE_HEIGHT_MAX = 560;
  const RESIZER_TRAY_HEIGHT = 28;
  const PAGE_BOTTOM_PADDING = 24;
  const WORKSPACE_HEIGHT_DEFAULT = WORKSPACE_HEIGHT_MAX;
  const [workspaceHeight, setWorkspaceHeight] = useState(WORKSPACE_HEIGHT_DEFAULT);
  const [refresh, setRefresh] = useState(false);
  // const [checkboxes, setCheckboxes] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState({});
  const [aiAnalyzeLoading, setAiAnalyzeLoading] = useState(false);
  const [aiConfirmModal, setAiConfirmModal] = useState({
    open: false,
    balanceAud: null,
    walletLoading: false,
    walletError: null,
  });
  const [aiProgressModal, setAiProgressModal] = useState(INITIAL_AI_PROGRESS_STATE);
  const aiStreamAbortRef = useRef(null);
  const unapproveConfirmResolverRef = useRef(null);
  const [unapproveConfirmModal, setUnapproveConfirmModal] = useState({
    open: false,
    variant: "single",
    requiresFundingWarning: false,
    ambiguousCount: 0,
    details: null,
    bulkRows: [],
    eligibleCount: 0,
    blockedCount: 0,
    confirming: false,
  });
  const approveConfirmResolverRef = useRef(null);
  const [approveConfirmModal, setApproveConfirmModal] = useState({
    open: false,
    scheduleMismatch: false,
    details: null,
  });
  const [aiBatchResult, setAiBatchResult] = useState(null);
  const [aiBatchTimesheets, setAiBatchTimesheets] = useState([]);
  const [aiStatusByTimesheetId, setAiStatusByTimesheetId] = useState({});
  const [aiReportDrawer, setAiReportDrawer] = useState({
    open: false,
    timesheetId: null,
    label: "",
    participants: [],
    initialStatus: null,
  });
  const [detailTabNavigateId, setDetailTabNavigateId] = useState(null);
  const [scrollToTabsAfterAi, setScrollToTabsAfterAi] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [sidebarWorkers, setSidebarWorkers] = useState([]);
  const [kpiSummary, setKpiSummary] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);

  const handleSidebarWorkersChange = useCallback((workers) => {
    setSidebarWorkers((prev) => {
      if (
        prev.length === workers.length &&
        prev.every((w, i) => w?.id === workers[i]?.id)
      ) {
        return prev;
      }
      return workers;
    });
  }, []);

  const timesheetsData = useMemo(
    () => timesheetDetailsList?.timesheets || {},
    [timesheetDetailsList]
  );

  const selectedParticipantKey = useMemo(
    () => participantFilterKey(selectedParticipants),
    [selectedParticipants]
  );

  /** Date + participant — worker / status / page changes must not remount the sidebar. */
  const workspaceContextKey = useMemo(
    () =>
      [
        dateRange?.fromDate,
        dateRange?.toDate,
        selectedParticipantKey,
      ].join("|"),
    [
      dateRange?.fromDate,
      dateRange?.toDate,
      selectedParticipantKey,
    ]
  );

  const kpiFilterKey = useMemo(
    () =>
      [
        dateRange?.fromDate,
        dateRange?.toDate,
        selectedParticipantKey,
        selectedWorkerInSidebar?.id ?? "all",
      ].join("|"),
    [
      dateRange?.fromDate,
      dateRange?.toDate,
      selectedParticipantKey,
      selectedWorkerInSidebar?.id,
    ]
  );

  const [hasWorkspaceLoaded, setHasWorkspaceLoaded] = useState(false);
  const lastWorkspaceContextRef = useRef(workspaceContextKey);

  useEffect(() => {
    if (lastWorkspaceContextRef.current !== workspaceContextKey) {
      lastWorkspaceContextRef.current = workspaceContextKey;
      setHasWorkspaceLoaded(false);
      setKpiSummary(null);
      setKpiLoading(true);
    }
  }, [workspaceContextKey]);

  useEffect(() => {
    if (!workersLoader && timeSheetsListOfWorkers?.workers != null) {
      setHasWorkspaceLoaded(true);
    }
  }, [workersLoader, timeSheetsListOfWorkers?.workers]);

  const fetchKpiSummary = useCallback(async () => {
    if (!dateRange?.fromDate || !dateRange?.toDate) {
      setKpiLoading(false);
      return;
    }
    setKpiLoading(true);
    try {
      const res = await apiPostRequest(URL_ROSTERING_TIMESHEET_KPI_SUMMARY, {
        timesheet_date_range: [dateRange?.fromDate, dateRange?.toDate],
        worker_id: selectedWorkerInSidebar?.id
          ? [selectedWorkerInSidebar.id]
          : "all",
        status: "all",
        pt_id: resolveTimesheetPtIdFilter(selectedParticipants),
      });
      const data = res?.data?.data ?? res?.data;
      if (data) {
        setKpiSummary({
          total_time_sheet: data.total_time_sheet,
          approved_time_sheet: data.approved_time_sheet,
          pending_time_sheet: data.pending_time_sheet,
          expired_time_sheet: data.expired_time_sheet,
          total_time_sheet_hours: data.total_time_sheet_hours,
          approved_time_sheet_hours: data.approved_time_sheet_hours,
          total_sleepover_count: data.total_sleepover_count,
          approved_sleepover_count: data.approved_sleepover_count,
          workers_with_pending: data.workers_with_pending,
          workers_in_scope: data.workers_in_scope,
        });
      }
    } catch {
      /* KPI strip keeps last values or empty */
    } finally {
      setKpiLoading(false);
    }
  }, [
    dateRange?.fromDate,
    dateRange?.toDate,
    selectedParticipants,
    selectedWorkerInSidebar?.id,
  ]);

  useEffect(() => {
    fetchKpiSummary();
  }, [kpiFilterKey, fetchKpiSummary]);

  const sidebarTotals = useMemo(
    () => sumSidebarWorkerTotals(sidebarWorkers),
    [sidebarWorkers]
  );

  useEffect(() => {
    if (participantList) {
      const participantsData = participantList?.participant?.data || [];
      const allOption = {
        label: `All ${(participantLabelPlural || "Participants").toLowerCase()}`,
        value: "all",
      };
      const modifiedParticipants = [
        allOption,
        ...participantsData
          ?.filter((participant) => participant.status === "active")
          ?.map((participant) => ({
            ...participant,
            label: `${capitalizeAllLetters(
              participant?.first_name
            )} ${capitalizeAllLetters(participant?.last_name)}`,
            value: participant.id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
      setParticipants(modifiedParticipants);
      // Keep "All" label in sync with care-sector wording when still on All.
      setSelectedParticipants((prev) =>
        isAllParticipantsSelection(prev) ? [allOption] : prev
      );
    }
  }, [participantList, participantLabelPlural, setSelectedParticipants]);

  useEffect(() => {
    const filters = {
      page: 1,
      per_page: 5000,
      search: null,
    };
    getParticipantList({
      filters,
      bodyFilter: spRosteringSetting?.only_core_pt_visibility
        ? { participant_type: [1] }
        : null,
    });
  }, [spRosteringSetting?.only_core_pt_visibility]);

  useEffect(() => {
    if (!selectedWorkerInSidebar?.id) return;
    setCheckboxes([]);
    // Deep-link select runs after this worker is set. Clearing here would
    // wipe the row we are about to open, and a string/number id flicker
    // after that would close the detail panel.
    if (deepLinkRef.current?.timesheetUuid && !deepLinkRowSelectedRef.current) {
      return;
    }
    dispatch(resetSingleCheckboxSelectedInTimesheet());
  }, [selectedWorkerInSidebar?.id, dispatch]);

  const handleRowSelect = useCallback(
    (item) => {
      if (!item?.uuid) return;

      // Row click opens/closes detail only — never toggles bulk checkboxes.
      if (checkboxes.length > 1) {
        setCheckboxes([]);
      }

      if (viewedTimesheet?.uuid === item.uuid) {
        dispatch(resetSingleCheckboxSelectedInTimesheet());
        return;
      }

      dispatch(singleCheckboxSelectedInTimesheet(item));
    },
    [checkboxes.length, viewedTimesheet?.uuid, dispatch, setCheckboxes]
  );

  const handleCheckboxSelectionChange = useCallback(
    (newCheckboxes) => {
      setCheckboxes(newCheckboxes);

      if (newCheckboxes.length === 0) {
        dispatch(resetSingleCheckboxSelectedInTimesheet());
      } else if (newCheckboxes.length === 1) {
        dispatch(singleCheckboxSelectedInTimesheet(newCheckboxes[0]));
      } else {
        dispatch(resetSingleCheckboxSelectedInTimesheet());
      }
    },
    [dispatch, setCheckboxes]
  );

  const showDetailPanel =
    checkboxes.length <= 1 && Boolean(viewedTimesheet?.uuid);

  useEffect(() => {
    if (!scrollToTabsAfterAi || checkboxes.length !== 1) return undefined;
    const timer = setTimeout(() => {
      scrollDetailPanelForAiNavigation(tabSectionRef.current);
      scrollToTabsAfterAiRef.current = false;
      setScrollToTabsAfterAi(false);
    }, AI_DRAWER_SCROLL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [scrollToTabsAfterAi, checkboxes.length]);

  useEffect(() => {
    if (timesheetsData) {
      const {
        total = 0,
        total_pages: totalPages = 0,
        per_page: perPage = WORKER_TIMESHEETS_LIMIT,
        current_page: currentPage = 1,
      } = timesheetsData;

      setPaginationObj((prev) => ({
        ...prev,
        total,
        totalPages,
        perPage,
        page: currentPage,
      }));
    }
  }, [timesheetsData]);

  useEffect(() => {
    if (dateRange?.fromDate && dateRange?.toDate) {
      dispatch(
        singleSelectedTimesheetOfWorkerCall({
          page,
          per_page: paginationObj.perPage || WORKER_TIMESHEETS_LIMIT,
          timesheet_date_range: [dateRange?.fromDate, dateRange?.toDate],
          worker_id: selectedWorkerInSidebar?.id
            ? [selectedWorkerInSidebar.id]
            : "all",
          search: null,
          status: selectedOption.value === "all" ? "all" : selectedOption.value,
          pt_id: resolveTimesheetPtIdFilter(selectedParticipants),
          sort_field: "time_sheet_date",
          sort_order: "desc",
        })
      );
    }
  }, [
    dateRange?.fromDate,
    dateRange?.toDate,
    selectedWorkerInSidebar?.id,
    selectedOption.value,
    page,
    selectedParticipantKey,
  ]);

  const onMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    const deltaY = e.clientY - startY.current;
    let newHeight = startHeight.current + deltaY;
    newHeight = Math.min(
      Math.max(newHeight, WORKSPACE_HEIGHT_MIN),
      WORKSPACE_HEIGHT_MAX
    );
    setWorkspaceHeight(newHeight);
  }, [WORKSPACE_HEIGHT_MIN, WORKSPACE_HEIGHT_MAX]);

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove]);

  const onMouseDown = useCallback(
    (e) => {
      isDragging.current = true;
      startY.current = e.clientY;
      startHeight.current = workspaceHeight;
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [onMouseMove, onMouseUp, workspaceHeight]
  );

  useLayoutEffect(() => {
    if (hasAutoSizedWorkspaceRef.current) return;
    if (typeof window === "undefined" || window.innerWidth < 1024) return;

    const showInitialSkeleton = spRosteringSettingLoader;
    const showWorkspaceSkeleton =
      !showInitialSkeleton && !hasWorkspaceLoaded && workersLoader;
    if (showInitialSkeleton || showWorkspaceSkeleton) return;

    const el = workspaceRef.current;
    if (!el) return;

    const top = el.getBoundingClientRect().top;
    const available =
      window.innerHeight - top - RESIZER_TRAY_HEIGHT - PAGE_BOTTOM_PADDING;
    const nextHeight = Math.min(
      WORKSPACE_HEIGHT_MAX,
      Math.max(WORKSPACE_HEIGHT_MIN, Math.floor(available))
    );

    setWorkspaceHeight(nextHeight);
    hasAutoSizedWorkspaceRef.current = true;
  }, [
    spRosteringSettingLoader,
    hasWorkspaceLoaded,
    workersLoader,
    WORKSPACE_HEIGHT_MIN,
    WORKSPACE_HEIGHT_MAX,
  ]);

  const commitDateRange = useCallback((from, to) => {
    if (!from || !to) return;
    lastAppliedRangeRef.current = { from, to };
    setFromDate(from);
    setToDate(to);
    selectedTimesheetDateRange({ fromDate: from, toDate: to });
  }, [selectedTimesheetDateRange]);

  useEffect(() => {
    if (!spRosteringSetting) return;

    const applyDateRange = (from, to) => {
      const last = lastAppliedRangeRef.current;
      if (last.from === from && last.to === to) {
        // Redux may have been cleared on route exit while local ref still matches.
        commitDateRange(from, to);
        return;
      }
      commitDateRange(from, to);
    };

    const deep = deepLinkRef.current;
    if (deep?.date) {
      applyDateRange(deep.date, deep.date);
      skipDefaultRangeRef.current = true;
      if (deep.workerId) {
        setSelectedWorkerInSidebar((prev) =>
          sameTimesheetWorkerId(prev?.id, deep.workerId)
            ? prev
            : { id: deep.workerId }
        );
      }
      setPage(1);
      return;
    }

    if (deep?.workerId) {
      setSelectedWorkerInSidebar((prev) =>
        sameTimesheetWorkerId(prev?.id, deep.workerId)
          ? prev
          : { id: deep.workerId }
      );
    }

    if (skipDefaultRangeRef.current) return;

    if (spRosteringSetting.calendar_type === "weekly") {
      const startOfWeek = moment().startOf("isoWeek");
      const endOfWeek = moment(startOfWeek).endOf("isoWeek");
      const from = startOfWeek.format("YYYY-MM-DD");
      const to = endOfWeek.format("YYYY-MM-DD");
      applyDateRange(from, to);
    } else {
      // const startOfWeek = moment().startOf("isoWeek");
      // const endOfWeek = moment(startOfWeek).add(13, "days");
      // const currentMonday = startOfWeek.clone();

      const today = moment();
      if (spRosteringSetting.default_fortnight === "f1") {
        const referenceMonday = moment("2025-01-06");

        const daysSinceRef = today.diff(referenceMonday, "days");
        const fortnightsSinceRef = Math.floor(daysSinceRef / 14);

        const currentMonday = moment(referenceMonday).add(
          fortnightsSinceRef * 14,
          "days"
        );

        const from = currentMonday.format("YYYY-MM-DD");
        const to = moment(currentMonday).add(13, "days").format("YYYY-MM-DD");
        applyDateRange(from, to);
      } else if (spRosteringSetting.default_fortnight === "f2") {
        const referenceMondayPrevious = moment("2025-01-13");
        const daysSinceRefPrevious = today.diff(
          referenceMondayPrevious,
          "days"
        );
        const fortnightsSinceRefPrevious = Math.floor(
          daysSinceRefPrevious / 14
        );
        const previousMonday = moment(referenceMondayPrevious).add(
          fortnightsSinceRefPrevious * 14,
          "days"
        );
        const previousFortnightEnd = moment(previousMonday).add(13, "days");
        // const previousMonday = currentMonday.clone().subtract(7, "days");
        // const endOfPreviousWeek = moment(previousMonday).add(13, "days");
        const from = previousMonday.format("YYYY-MM-DD");
        const to = previousFortnightEnd.format("YYYY-MM-DD");

        applyDateRange(from, to);
      }
    }
  }, [spRosteringSetting, setPage, setSelectedWorkerInSidebar, commitDateRange]);

  useEffect(() => {
    const workerId = deepLinkRef.current?.workerId;
    if (!workerId || deepLinkRowSelectedRef.current) return;
    const match = sidebarWorkers.find((worker) =>
      sameTimesheetWorkerId(worker?.id, workerId)
    );
    if (!match) return;
    setSelectedWorkerInSidebar((prev) => {
      if (sameTimesheetWorkerId(prev?.id, match.id) && prev?.first_name) {
        return prev;
      }
      return { ...match, id: Number(match.id) };
    });
  }, [sidebarWorkers, setSelectedWorkerInSidebar]);

  useEffect(() => {
    const deep = deepLinkRef.current;
    if (!deep?.timesheetUuid || deepLinkRowSelectedRef.current) return;
    if (tableTimesheetsLoader) return;
    if (!Array.isArray(timesheetsData?.data)) return;
    if (
      deep.date &&
      (dateRange?.fromDate !== deep.date || dateRange?.toDate !== deep.date)
    ) {
      return;
    }
    if (
      deep.workerId &&
      !sameTimesheetWorkerId(selectedWorkerInSidebar?.id, deep.workerId)
    ) {
      return;
    }

    const match = timesheetsData.data.find(
      (row) => row?.uuid === deep.timesheetUuid
    );
    // Detail loads by uuid, so a row off this page still opens.

    deepLinkRowSelectedRef.current = true;
    dispatch(
      singleCheckboxSelectedInTimesheet(match || { uuid: deep.timesheetUuid })
    );
    deepLinkRef.current = null;
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, [
    dateRange?.fromDate,
    dateRange?.toDate,
    dispatch,
    location.pathname,
    location.search,
    navigate,
    selectedWorkerInSidebar?.id,
    tableTimesheetsLoader,
    timesheetsData,
  ]);

  const handlePagination = useCallback(
    (newPage) => {
      setPage(newPage);
      setCheckboxes([]);
      dispatch(resetSingleCheckboxSelectedInTimesheet());
      setPaginationObj((prev) => ({
        ...prev,
        page: newPage,
      }));
    },
    [dispatch, setCheckboxes, setPage]
  );

  useEffect(() => {
    if (updatedAction !== "pending" && checkboxes?.length > 1) {
      handleStatusChange(checkboxes, updatedAction, true);
    }
  }, [updatedAction]);

  useEffect(() => {
    if (!showDetailPanel || !tabSectionRef.current) return;
    if (scrollToTabsAfterAiRef.current) return;
    scrollDetailPanelIntoViewIfNeeded(tabSectionRef.current, {
      behavior: "smooth",
      block: "nearest",
    });
  }, [showDetailPanel, viewedTimesheet?.uuid]);

  const {
    canAnalyse,
    canBulkAnalyse,
    canViewInsights,
    loading: aiAccessLoading,
  } = useTimesheetAiAccess();

  const bulkAnalysePageRange = useMemo(
    () => ({ from: dateRange?.fromDate, to: dateRange?.toDate }),
    [dateRange?.fromDate, dateRange?.toDate]
  );

  // A bulk run finished: reload the table so its reports show in the AI column.
  const handleBulkRunFinished = useCallback(() => {
    if (!dateRange?.fromDate || !dateRange?.toDate) return;
    dispatch(
      singleSelectedTimesheetOfWorkerCall({
        page,
        per_page: paginationObj.perPage || WORKER_TIMESHEETS_LIMIT,
        timesheet_date_range: [dateRange?.fromDate, dateRange?.toDate],
        worker_id: selectedWorkerInSidebar?.id ? [selectedWorkerInSidebar.id] : "all",
        search: null,
        status: selectedOption.value === "all" ? "all" : selectedOption.value,
        pt_id: resolveTimesheetPtIdFilter(selectedParticipants),
        sort_field: "time_sheet_date",
        sort_order: "desc",
      })
    );
    fetchKpiSummary();
  }, [
    dispatch,
    dateRange?.fromDate,
    dateRange?.toDate,
    page,
    paginationObj.perPage,
    selectedWorkerInSidebar?.id,
    selectedOption.value,
    selectedParticipants,
    fetchKpiSummary,
  ]);

  useEffect(() => {
    if (aiAccessLoading) return;
    if (!canAnalyse) {
      setAiBatchResult(null);
      setAiBatchTimesheets([]);
    }
    if (!canViewInsights) {
      setAiReportDrawer({
        open: false,
        timesheetId: null,
        label: "",
        participants: [],
        initialStatus: null,
      });
    }
  }, [aiAccessLoading, canAnalyse, canViewInsights]);

  const allSelectedPending = useMemo(
    () =>
      checkboxes.length > 0 &&
      checkboxes.every(
        (item) => String(item?.status || "").toLowerCase() === "pending"
      ),
    [checkboxes]
  );

  const allSelectedUnapprovable = useMemo(
    () =>
      checkboxes.length > 0 &&
      checkboxes.every((item) => isUnapproveBulkStatus(item?.status)),
    [checkboxes]
  );

  const bulkToolbarMode = useMemo(() => {
    const selectableStatus = getSelectableStatusForFilter(selectedOption.value);
    return isUnapproveBulkStatus(selectableStatus) ? "unapprove" : "approve";
  }, [selectedOption.value]);

  const canAnalyzeWithAi = checkboxes.length >= 1 && allSelectedPending;
  const aiAnalyseInFlight = aiProgressModal.open;

  const applyBatchPayload = useCallback((batchPayload) => {
    setAiBatchResult(batchPayload);
    setAiStatusByTimesheetId((prev) =>
      mergeBatchResultsIntoAiStatus(prev, batchPayload)
    );
  }, []);

  const runStreamAnalyse = useCallback(
    async ({ timesheetIds, timesheetRows = [] }) => {
      setAiConfirmModal((prev) => ({ ...prev, open: false }));
      releaseBodyScrollLock();
      setAiProgressModal({
        ...INITIAL_AI_PROGRESS_STATE,
        open: true,
        timesheetTotal: timesheetIds.length,
      });
      setAiAnalyzeLoading(true);

      const rowById = new Map(
        timesheetRows.map((row) => [Number(row?.id), row])
      );

      const abortController = new AbortController();
      aiStreamAbortRef.current = abortController;

      try {
        const complete = await aiApi.runTimesheetAnomalyCheckBatchStream(
          timesheetIds,
          {
            signal: abortController.signal,
            onEvent: (event) => {
              if (event?.type !== "progress") return;
              const row = rowById.get(Number(event.timesheet_id));
              const { title } = buildTimesheetRowLabel(row);
              const nextPercent = Math.min(
                AI_PROGRESS_STREAM_CAP,
                Number(event.percent ?? 0) || 0
              );
              setAiProgressModal((prev) =>
                applyTimesheetAiProgressEvent(prev, event, {
                  nextPercent,
                  timesheetLabel: title,
                })
              );
            },
          }
        );

        const payload = complete?.data ?? {};
        const batchPayload = {
          results: payload?.results || [],
          summary: payload?.summary || {},
        };
        const succeeded = Number(batchPayload.summary?.succeeded ?? 0);
        const failedItems = (batchPayload.results || []).filter(
          (item) => item?.success !== true
        );

        if (succeeded === 0 && failedItems.length > 0) {
          const firstFail = failedItems[0];
          const err = new Error(
            resolveTimesheetAiErrorMessage(firstFail, "AI analysis unavailable.")
          );
          err.code = firstFail?.code || "AI_UNAVAILABLE";
          err.toastShown = true;
          applyBatchPayload(batchPayload);
          await runAiProgressFinishSequence(setAiProgressModal, {
            error: err.message,
            errorCode: err.code,
          });
          toast.error(err.message);
          throw err;
        }

        await runAiProgressFinishSequence(setAiProgressModal);

        applyBatchPayload(batchPayload);
        setAiProgressModal(INITIAL_AI_PROGRESS_STATE);
        // Progress modal afterLeave also releases; this covers Headless restore race.
        releaseBodyScrollLockAfterTransition();
        if (failedItems.length > 0) {
          toast.success(
            `AI analysis completed for ${succeeded} of ${batchPayload.results.length} timesheet(s).`
          );
        } else {
          toast.success("AI anomaly analysis completed.");
        }
        return batchPayload;
      } catch (error) {
        if (error?.code === "CANCELLED") {
          setAiProgressModal(INITIAL_AI_PROGRESS_STATE);
          releaseBodyScrollLockAfterTransition();
          return null;
        }
        if (error?.data?.results || error?.data?.summary) {
          applyBatchPayload({
            results: error.data.results || [],
            summary: error.data.summary || {},
          });
        }
        setAiProgressModal((prev) => ({
          ...prev,
          error: resolveTimesheetAiErrorMessage(
            error,
            error?.message || "Failed to run AI anomaly analysis."
          ),
          errorCode: error?.code || null,
          status: error?.status || null,
        }));

        if (error?.code !== "CANCELLED" && !error?.toastShown) {
          toast.error(
            resolveTimesheetAiErrorMessage(
              error,
              error?.message || "Failed to run AI anomaly analysis."
            )
          );
        }
        throw error;
      } finally {
        aiStreamAbortRef.current = null;
        setAiAnalyzeLoading(false);
      }
    },
    [applyBatchPayload]
  );

  const handleAnalyzeWithAi = async () => {
    if (!canAnalyzeWithAi) {
      toast.error("Select at least one pending timesheet to analyse.");
      return;
    }

    if (!canAnalyse) {
      toast.error("You do not have permission to run AI anomaly checks.");
      return;
    }

    setAiConfirmModal({
      open: true,
      balanceAud: null,
      walletLoading: true,
      walletError: null,
    });

    try {
      const walletRes = await apiGetRequest(URL_SP_AI_WALLET);
      const walletPayload = walletRes?.data?.data ?? walletRes?.data ?? {};
      const wallet = walletPayload?.wallet ?? {};
      const balanceAud = Number(wallet?.balance_aud || 0);
      setAiConfirmModal((prev) => ({
        ...prev,
        balanceAud,
        walletLoading: false,
        walletError: null,
      }));
    } catch (error) {
      setAiConfirmModal((prev) => ({
        ...prev,
        walletLoading: false,
        walletError:
          error?.message || "Could not load AI credit balance. Try again.",
      }));
    }
  };

  const handleCloseAiConfirmModal = () => {
    if (aiAnalyzeLoading) return;
    setAiConfirmModal((prev) => ({ ...prev, open: false }));
  };

  const handleConfirmAnalyzeWithAi = async () => {
    if (!canAnalyzeWithAi || aiAnalyzeLoading) return;

    const timesheetIds = checkboxes
      .map((timesheet) => Number(timesheet.id))
      .filter((id) => Number.isFinite(id) && id > 0);

    setAiBatchResult(null);
    setAiBatchTimesheets([...checkboxes]);

    try {
      await runStreamAnalyse({
        timesheetIds,
        timesheetRows: checkboxes,
      });
    } catch {
      // Errors surfaced in progress modal + toast.
    }
  };

  const handleCloseAiProgressModal = () => {
    setAiProgressModal(INITIAL_AI_PROGRESS_STATE);
    releaseBodyScrollLockAfterTransition();
  };

  const requestUnapproveConfirmation = useCallback((options) => {
    return new Promise((resolve) => {
      unapproveConfirmResolverRef.current = resolve;
      setUnapproveConfirmModal({
        open: true,
        variant: options?.variant || "single",
        requiresFundingWarning: !!options?.requiresFundingWarning,
        ambiguousCount: options?.ambiguousCount ?? 0,
        details: options?.details ?? null,
        bulkRows: options?.bulkRows ?? [],
        eligibleCount: options?.eligibleCount ?? 0,
        blockedCount: options?.blockedCount ?? 0,
        confirming: false,
      });
    });
  }, []);

  const handleCloseUnapproveConfirmModal = useCallback(() => {
    setUnapproveConfirmModal((prev) => {
      if (prev.confirming) return prev;
      unapproveConfirmResolverRef.current?.(false);
      unapproveConfirmResolverRef.current = null;
      return { ...prev, open: false, confirming: false };
    });
  }, []);

  const handleConfirmUnapproveModal = useCallback(() => {
    unapproveConfirmResolverRef.current?.(true);
    unapproveConfirmResolverRef.current = null;
  }, []);

  const requestApproveConfirmation = useCallback((options) => {
    return new Promise((resolve) => {
      approveConfirmResolverRef.current = resolve;
      setApproveConfirmModal({
        open: true,
        scheduleMismatch: !!options?.scheduleMismatch,
        details: options?.details ?? null,
      });
    });
  }, []);

  const handleCloseApproveConfirmModal = useCallback(() => {
    approveConfirmResolverRef.current?.(false);
    approveConfirmResolverRef.current = null;
    setApproveConfirmModal((prev) => ({ ...prev, open: false }));
  }, []);

  const handleConfirmApproveModal = useCallback(() => {
    approveConfirmResolverRef.current?.(true);
    approveConfirmResolverRef.current = null;
    setApproveConfirmModal((prev) => ({ ...prev, open: false }));
  }, []);

  const handleCancelAiProgress = async () => {
    if (aiProgressModal.error) {
      handleCloseAiProgressModal();
      return;
    }

    const confirmed = await swal({
      title: "Cancel AI analyse?",
      text: "Analyse is still in progress. Are you sure you want to cancel?",
      icon: "warning",
      dangerMode: true,
      buttons: ["Keep analysing", "Yes, cancel"],
    });

    if (!confirmed) return;

    aiStreamAbortRef.current?.abort();
    setAiProgressModal(INITIAL_AI_PROGRESS_STATE);
    setAiAnalyzeLoading(false);
    releaseBodyScrollLockAfterTransition();
    toast.info("AI analyse cancelled.");
  };

  const handleAiReAnalyzeComplete = useCallback((timesheetId, payload) => {
    const status = buildAiStatusFromBatchItem({
      success: true,
      data: payload,
      timesheet_id: timesheetId,
    });
    setAiStatusByTimesheetId((prev) => ({
      ...prev,
      [timesheetId]: status,
    }));
  }, []);

  const handleStreamReAnalyze = useCallback(
    async (timesheetId) => {
      if (!canAnalyse) {
        toast.error("You do not have permission to run AI anomaly checks.");
        return null;
      }

      const id = Number(timesheetId);
      if (!Number.isFinite(id) || id <= 0) return null;

      const row =
        timesheetsData?.data?.find((item) => Number(item?.id) === id) ||
        aiBatchTimesheets.find((item) => Number(item?.id) === id) ||
        (Number(aiReportDrawer.timesheetId) === id
          ? {
              id,
              participants: aiReportDrawer.participants,
            }
          : null);

      const batchPayload = await runStreamAnalyse({
        timesheetIds: [id],
        timesheetRows: row ? [row] : [],
      });

      const item = (batchPayload?.results || []).find(
        (result) => Number(result?.timesheet_id) === id
      );
      if (item?.success && item?.data) {
        handleAiReAnalyzeComplete(id, item.data);
        return item.data;
      }
      return null;
    },
    [
      aiBatchTimesheets,
      aiReportDrawer.participants,
      aiReportDrawer.timesheetId,
      canAnalyse,
      handleAiReAnalyzeComplete,
      runStreamAnalyse,
      timesheetsData?.data,
    ]
  );

  const handleOpenAiReport = (timesheetId, status) => {
    if (!canViewInsights) {
      toast.error("You do not have permission to view AI timesheet insights.");
      return;
    }

    const row =
      timesheetsData?.data?.find(
        (item) => Number(item?.id) === Number(timesheetId)
      ) ||
      aiBatchTimesheets.find((item) => Number(item?.id) === Number(timesheetId));
    const label = buildTimesheetRowLabel(row).title;
    setAiReportDrawer({
      open: true,
      timesheetId,
      label,
      participants: row?.participants || [],
      initialStatus: status,
    });
  };

  const handleCloseAiReport = () => {
    setAiReportDrawer({
      open: false,
      timesheetId: null,
      label: "",
      participants: [],
      initialStatus: null,
    });
  };

  const handleNavigateToTabFromAi = (timesheetId, tabId) => {
    const row = timesheetsData?.data?.find(
      (item) => Number(item?.id) === Number(timesheetId)
    );
    if (!row) {
      toast.info(
        "This timesheet is not on the current page. Select it from the list first."
      );
      return;
    }

    const selectedId = Number(checkboxes[0]?.id);
    if (selectedId !== Number(timesheetId)) {
      setCheckboxes([row]);
      dispatch(singleCheckboxSelectedInTimesheet(row));
    }

    setDetailTabNavigateId(tabId);
    scrollToTabsAfterAiRef.current = true;
    setScrollToTabsAfterAi(true);
    handleCloseAiReport();
  };

  const handleTabNavigateConsumed = useCallback(() => {
    setDetailTabNavigateId(null);
  }, []);

  const buildTimesheetRefreshQueryPayload = useCallback(
    () => ({
      query_params_timesheet_workers: {
        page: 1,
        per_page: Math.max(
          WORKER_SIDEBAR_LIMIT,
          sidebarLoadedCount || WORKER_SIDEBAR_LIMIT
        ),
        timesheet_date_range: [dateRange?.fromDate, dateRange?.toDate],
        pt_id:
          lastWorkersQuery?.pt_id ??
          resolveTimesheetPtIdFilter(selectedParticipants),
        search: lastWorkersQuery?.search ?? null,
        sort_field: lastWorkersQuery?.sort_field ?? "first_name",
        sort_order: lastWorkersQuery?.sort_order ?? "asc",
      },
      query_params_worker_timesheets: {
        page,
        per_page: paginationObj.perPage || WORKER_TIMESHEETS_LIMIT,
        timesheet_date_range: [dateRange?.fromDate, dateRange?.toDate],
        worker_id: selectedWorkerInSidebar?.id
          ? [selectedWorkerInSidebar.id]
          : "all",
        search: null,
        status: selectedOption.value === "all" ? "all" : selectedOption.value,
        pt_id: resolveTimesheetPtIdFilter(selectedParticipants),
        sort_field: "time_sheet_date",
        sort_order: "desc",
      },
    }),
    [
      dateRange?.fromDate,
      dateRange?.toDate,
      lastWorkersQuery?.pt_id,
      lastWorkersQuery?.search,
      lastWorkersQuery?.sort_field,
      lastWorkersQuery?.sort_order,
      page,
      paginationObj.perPage,
      selectedOption.value,
      selectedParticipants,
      selectedWorkerInSidebar?.id,
      sidebarLoadedCount,
    ]
  );

  const applyTimesheetListRefresh = useCallback(
    (res, { clearSelection = true, resetBulkAction = false } = {}) => {
      if (res?.status !== 200) {
        return false;
      }

      const workerTimesheets = res?.data?.data?.get_worker_timesheets;
      const timesheets = workerTimesheets?.timesheets;
      const nextPage = resolveTimesheetPageAfterRefresh(page, timesheets);

      dispatch(
        getTimesheetsOfWorkersAction.success(
          res?.data?.data?.get_timesheets_workers
        )
      );
      fetchKpiSummary();

      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        dispatch(
          singleSelectedTimesheetOfWorkerAction.success(workerTimesheets)
        );
      }

      if (clearSelection) {
        setCheckboxes([]);
      }
      if (resetBulkAction) {
        setUpdatedAction("pending");
      }
      setRefresh((prev) => !prev);
      return true;
    },
    [dispatch, fetchKpiSummary, page, setCheckboxes, setPage]
  );

  const handleStatusChange = async (
    item,
    status,
    isAction = false,
    isApproveNext = false,
    options = {}
  ) => {
    const uuids = isAction ? item.map((i) => i.uuid) : [item.uuid];
    const loadingKey = uuids.join(",");
    const isUnapprove = status === "pending";

    if (!isUnapprove) {
      setLoadingStatus((prev) => ({ ...prev, [loadingKey]: true }));
    }

    const payload = {
      uuid: uuids,
      status,
      ...buildTimesheetRefreshQueryPayload(),
    };

    if (status === "approved") {
      const preference = options?.approved_time_preference;
      if (preference === "scheduled" || preference === "actual") {
        payload.approved_time_preference = preference;
      }
    }

    const submitStatusUpdate = (ackMultiFunding) => {
      const body =
        ackMultiFunding === true
          ? { ...payload, acknowledge_multi_funding_ambiguity: true }
          : payload;
      return apiPostRequest(URL_ROSTERING_TIMESHEET_STATUS_UPDATE, body);
    };

    const applySuccess = (res) => {
      if (
        applyTimesheetListRefresh(res, {
          clearSelection: !isApproveNext,
          resetBulkAction: isAction && !isApproveNext,
        })
      ) {
        toast.success(
          status === "approved"
            ? "Timesheet approved successfully"
            : status === "pending"
            ? "Timesheet unapproved successfully"
            : "Timesheet rejected successfully"
        );
      } else {
        console.error(`Failed to change status of ${uuids}`);
      }
    };

    try {
      if (status === "pending") {
        const row = isAction ? item[0] : item;
        if (isAction && uuids.length > 1) {
          toast.error("Use bulk unapprove for multiple timesheets.");
          return;
        }

        const assessRes = await apiPostRequest(
          URL_ROSTERING_TIMESHEET_UNAPPROVE_FUNDING_ASSESSMENT,
          { uuid: uuids }
        );
        const assessData =
          assessRes?.data?.data ?? assessRes?.data ?? null;
        const requiresFundingWarning =
          assessmentRequiresFundingWarning(assessData);
        const ambiguousCount =
          countAmbiguousLinesFromAssessment(assessData);
        const confirmed = await requestUnapproveConfirmation({
          variant: "single",
          requiresFundingWarning,
          ambiguousCount,
          details: buildUnapproveTimesheetDetails(row),
        });
        if (!confirmed) {
          return;
        }

        setUnapproveConfirmModal((prev) => ({ ...prev, confirming: true }));
        setLoadingStatus((prev) => ({ ...prev, [loadingKey]: true }));
        const res = await submitStatusUpdate(requiresFundingWarning);
        applySuccess(res);
      } else {
        const res = await submitStatusUpdate(false);
        applySuccess(res);
      }
    } catch (error) {
      console.error(`Error changing status of ${uuids}:`, error);
      toast.error(error?.message || "Failed to update status");
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [loadingKey]: false }));
      if (isUnapprove) {
        setUnapproveConfirmModal((prev) => ({ ...prev, open: false, confirming: false }));
      }
    }
  };

  const handleBulkUnapprove = async () => {
    if (checkboxes.length <= 1) {
      return;
    }
    if (checkboxes.length > BULK_UNAPPROVE_MAX) {
      toast.error(
        `You can unapprove at most ${BULK_UNAPPROVE_MAX} timesheets at once.`
      );
      return;
    }
    if (!allSelectedUnapprovable) {
      toast.error("Selected timesheets must all be approved, rejected, or expired.");
      return;
    }

    const uuids = checkboxes.map((item) => item.uuid);
    const loadingKey = uuids.join(",");
    setLoadingStatus((prev) => ({ ...prev, [loadingKey]: true }));

    try {
      const assessRes = await apiPostRequest(
        URL_ROSTERING_TIMESHEET_UNAPPROVE_FUNDING_ASSESSMENT,
        { uuid: uuids }
      );
      const assessData = assessRes?.data?.data ?? assessRes?.data ?? null;
      const bulkRows = buildBulkUnapproveModalRows(checkboxes, assessData);
      const eligibleUuids = getEligibleUnapproveUuids(bulkRows);
      const eligibleCount = eligibleUuids.length;
      const blockedCount = bulkRows.filter((row) => row.blocked).length;

      if (eligibleCount === 0) {
        toast.error("None of the selected timesheets can be unapproved.");
        return;
      }

      const requiresFundingWarning =
        assessmentRequiresFundingWarning(assessData) &&
        bulkRows.some(
          (row) => row.eligible && row.requiresFundingWarning
        );
      const ambiguousCount = countAmbiguousLinesFromAssessment(assessData);

      const confirmed = await requestUnapproveConfirmation({
        variant: "bulk",
        requiresFundingWarning,
        ambiguousCount,
        bulkRows,
        eligibleCount,
        blockedCount,
      });
      if (!confirmed) {
        return;
      }

      setUnapproveConfirmModal((prev) => ({ ...prev, confirming: true }));

      const body = {
        uuid: eligibleUuids,
        ...buildTimesheetRefreshQueryPayload(),
        ...(requiresFundingWarning
          ? { acknowledge_multi_funding_ambiguity: true }
          : {}),
      };
      const res = await apiPostRequest(URL_ROSTERING_TIMESHEET_BULK_UNAPPROVE, body);

      if (
        applyTimesheetListRefresh(res, {
          clearSelection: true,
          resetBulkAction: false,
        })
      ) {
        const succeeded =
          res?.data?.data?.summary?.succeeded ?? eligibleCount;
        toast.success(
          `${succeeded} timesheet${succeeded === 1 ? "" : "s"} unapproved successfully`
        );
      }
    } catch (error) {
      console.error(`Error bulk unapproving ${uuids}:`, error);
      toast.error(error?.message || "Failed to unapprove timesheets");
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [loadingKey]: false }));
      setUnapproveConfirmModal((prev) => ({ ...prev, open: false, confirming: false }));
    }
  };

  const activeListLength = timesheetsData?.data || [];
  const canNavigateAiReportTab = useMemo(() => {
    const drawerId = Number(aiReportDrawer.timesheetId);
    if (!Number.isFinite(drawerId) || drawerId <= 0) return false;
    return (timesheetsData?.data || []).some(
      (row) => Number(row?.id) === drawerId
    );
  }, [aiReportDrawer.timesheetId, timesheetsData?.data]);
  const [selectedFromDate, setSelectedFromDate] = useState(null);

  const bulkDisabled =
    bulkToolbarMode === "unapprove"
      ? checkboxes.length <= 1 ||
        !allSelectedUnapprovable ||
        loadingStatus[checkboxes.map((i) => i.uuid).join(",")]
      : checkboxes.length <= 1 ||
        !allSelectedPending ||
        checkboxes?.some(
          (item) => item.status === "N/A" || item.status === "In-progress"
        ) ||
        loadingStatus[checkboxes.map((i) => i.uuid).join(",")];

  const showInitialSkeleton = spRosteringSettingLoader;
  /** First load for date/participant — sidebar + table skeleton only. */
  const showWorkspaceSkeleton =
    !showInitialSkeleton && !hasWorkspaceLoaded && workersLoader;
  /** Worker / status / page refetch — table skeleton only. */
  const showTableRefetchSkeleton =
    tableTimesheetsLoader && hasWorkspaceLoaded;

  return (
    <div className="min-w-full overflow-x-hidden flex flex-col gap-3">
      {showInitialSkeleton ? (
        <TimesheetPageSkeleton />
      ) : (
        <>
      <TimesheetKpiStrip summary={kpiSummary} loading={kpiLoading} />

      <TimesheetFilterBar
        participants={participants}
        selectedParticipants={selectedParticipants}
        onParticipantChange={(e) => {
          setSelectedParticipants(e);
          setCheckboxes([]);
          dispatch(resetSingleCheckboxSelectedInTimesheet());
          setSelectedWorkerInSidebar(null);
        }}
        fromDate={fromDate}
        toDate={toDate}
        onDateRangeChange={({ fromDate: from, toDate: to }) => commitDateRange(from, to)}
        setCheckboxes={setCheckboxes}
        isAllreadySelectedTimesheet={checkboxes.length > 0}
        setSelectedFromDate={setSelectedFromDate}
        maxDate={addMonths(selectedFromDate || new Date(), 3)}
        setPage={setPage}
        statusOptions={NEW_TIMESHEET_STATUS}
        selectedStatusValue={selectedOption.value}
        onStatusChange={(e) => {
          setSelectedOptions(e);
          setCheckboxes([]);
          dispatch(resetSingleCheckboxSelectedInTimesheet());
          setPage(1);
        }}
      />

      {showWorkspaceSkeleton ? (
        <div
          className="timesheet-workspace flex flex-col gap-3 lg:flex-row lg:gap-4"
          style={{ "--timesheet-workspace-h": `${workspaceHeight}px` }}
        >
          <div className="timesheet-workspace-sidebar h-[min(360px,50vh)] w-full shrink-0 lg:w-[260px]">
            <TimesheetWorkerSidebarSkeleton />
          </div>
          <div className="timesheet-workspace-table min-h-[280px] h-[min(440px,52vh)] w-full min-w-0 lg:h-auto lg:min-h-0 lg:flex-1">
            <TimesheetTableSkeleton />
          </div>
        </div>
      ) : (
        <>
          <div
            ref={workspaceRef}
            className="timesheet-workspace flex flex-col gap-3 lg:flex-row lg:gap-4"
            style={{ "--timesheet-workspace-h": `${workspaceHeight}px` }}
          >
            <div className="timesheet-workspace-sidebar flex h-[min(360px,50vh)] w-full shrink-0 flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white lg:h-auto lg:w-[260px]">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <TimeSheetSideBar
                  getTimesheetsofWorkers={getTimesheetsofWorkers}
                  timeSheetsListOfWorkers={timeSheetsListOfWorkers}
                  setSelectedWorkerInSidebar={setSelectedWorkerInSidebar}
                  selectedWorkerInSidebar={selectedWorkerInSidebar}
                  spRosteringSettingLoader={spRosteringSettingLoader}
                  setPage={setPage}
                  setCheckboxes={setCheckboxes}
                  workersLoader={workersLoader}
                  selectedParticipants={selectedParticipants}
                  onSidebarLoadedCountChange={onSidebarLoadedCountChange}
                  onWorkersChange={handleSidebarWorkersChange}
                />
              </div>
              {(!workersLoader || sidebarLoadedCount > 0) && (
                <div
                  className="flex shrink-0 items-center justify-center gap-2.5 bg-gradient py-4 text-center text-xs font-bold text-white lg:py-3.5"
                >
                  <span>
                    {sidebarTotals.timesheetCount} Timesheets
                  </span>
                  <span className="h-1 w-1 rounded-full bg-white/60" />
                  <span>{sidebarTotals.hoursDisplay} Hours</span>
                </div>
              )}
            </div>

            <div className="timesheet-workspace-table relative flex h-[min(440px,52vh)] min-h-[280px] w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white lg:h-auto lg:min-h-0 lg:flex-1">
              <BulkAnalyseProvider
                enabled={canBulkAnalyse}
                pageRange={bulkAnalysePageRange}
                onOpenReport={handleOpenAiReport}
                onRunFinished={handleBulkRunFinished}
              >
              <TimesheetBulkToolbar
                selectedCount={checkboxes.length}
                setUpdatedAction={setUpdatedAction}
                bulkMode={bulkToolbarMode}
                onBulkUnapprove={handleBulkUnapprove}
                unapproveModalOpen={unapproveConfirmModal.open}
                disabled={bulkDisabled}
                loading={
                  loadingStatus[checkboxes.map((i) => i.uuid).join(",")]
                }
                showAiAnalyze={canAnalyse}
                aiAnalyzeDisabled={!canAnalyzeWithAi || aiAnalyseInFlight}
                aiAnalyzeLoading={aiAnalyzeLoading || aiAnalyseInFlight}
                onAnalyzeWithAi={handleAnalyzeWithAi}
                bulkAnalyseButton={
                  ["all", "pending"].includes(
                    String(selectedOption.value || "").toLowerCase()
                  ) ? (
                    <BulkAnalyseToolbarButton />
                  ) : null
                }
              />
              <BulkAnalyseStatusBar />

              <div className="custom-scroll min-h-0 flex-1 overflow-hidden">
                {showTableRefetchSkeleton ? (
                  <TimesheetTableBodySkeleton />
                ) : (
                  <ResizeableTable
                    fillHeight
                    timesheetDetailsList={timesheetsData}
                    checkboxes={checkboxes}
                    setCheckboxes={handleCheckboxSelectionChange}
                    viewedTimesheetUuid={viewedTimesheet?.uuid}
                    onRowSelect={handleRowSelect}
                    handleStatusChange={handleStatusChange}
                    requestApproveConfirmation={requestApproveConfirmation}
                    loadingStatus={loadingStatus}
                    participantLabelPlural={participantLabelPlural}
                    showAiColumn={canViewInsights}
                    aiStatusByTimesheetId={aiStatusByTimesheetId}
                    onOpenAiReport={handleOpenAiReport}
                    statusFilter={selectedOption.value}
                  />
                )}
              </div>

              {!showTableRefetchSkeleton &&
                (paginationObj.total > 0 || timesheetsData?.data?.length > 0) && (
                <div className="shrink-0 border-t border-[#E5E7EB] px-4 py-2">
                  <PaginationComponent
                    page={page}
                    newDesign={true}
                    activeListLength={activeListLength}
                    limit={WORKER_TIMESHEETS_LIMIT}
                    totalList={paginationObj.total || 0}
                    handlePagination={handlePagination}
                    totalPages={paginationObj.totalPages}
                  />
                </div>
              )}
              </BulkAnalyseProvider>
            </div>
          </div>

          {(timesheetsData?.data?.length > 0 || showDetailPanel) && (
            <div className="hidden lg:block">
              <TimesheetResizeTray onMouseDown={onMouseDown} />
            </div>
          )}

          {canViewInsights && aiBatchResult && (
            <TimesheetAiResultsPanel
              result={aiBatchResult}
              timesheets={aiBatchTimesheets}
              onViewReport={handleOpenAiReport}
              onClose={() => {
                setAiBatchResult(null);
                setAiBatchTimesheets([]);
              }}
            />
          )}

          <TimesheetAiReportDrawer
            open={aiReportDrawer.open}
            timesheetId={aiReportDrawer.timesheetId}
            timesheetLabel={aiReportDrawer.label}
            timesheetParticipants={aiReportDrawer.participants}
            initialStatus={aiReportDrawer.initialStatus}
            canReAnalyze={canAnalyse}
            canNavigateToTab={canNavigateAiReportTab}
            analyseInFlight={aiAnalyseInFlight}
            onClose={handleCloseAiReport}
            onReAnalyzeComplete={handleAiReAnalyzeComplete}
            onStreamReAnalyze={handleStreamReAnalyze}
            onNavigateToTab={handleNavigateToTabFromAi}
          />

          <TimesheetAiProgressModal
            isOpen={aiProgressModal.open}
            percent={aiProgressModal.percent}
            stage={aiProgressModal.stage}
            label={aiProgressModal.label}
            timesheetCompleted={aiProgressModal.timesheetCompleted}
            timesheetIndex={aiProgressModal.timesheetIndex}
            timesheetTotal={aiProgressModal.timesheetTotal}
            timesheetLabel={aiProgressModal.timesheetLabel}
            error={aiProgressModal.error}
            onClose={handleCloseAiProgressModal}
            onCancelRequest={handleCancelAiProgress}
          />

          <TimesheetAiConfirmModal
            isOpen={aiConfirmModal.open}
            onClose={handleCloseAiConfirmModal}
            onConfirm={handleConfirmAnalyzeWithAi}
            timesheetCount={checkboxes.length}
            timesheets={checkboxes}
            balanceAud={aiConfirmModal.balanceAud}
            walletLoading={aiConfirmModal.walletLoading}
            walletError={aiConfirmModal.walletError}
            confirming={aiAnalyzeLoading}
            variant={checkboxes.length === 1 ? "single" : "batch"}
          />

          <TimesheetUnapproveConfirmModal
            isOpen={unapproveConfirmModal.open}
            onClose={handleCloseUnapproveConfirmModal}
            onConfirm={handleConfirmUnapproveModal}
            confirming={unapproveConfirmModal.confirming}
            variant={unapproveConfirmModal.variant}
            requiresFundingWarning={unapproveConfirmModal.requiresFundingWarning}
            ambiguousCount={unapproveConfirmModal.ambiguousCount}
            details={unapproveConfirmModal.details}
            bulkRows={unapproveConfirmModal.bulkRows}
            eligibleCount={unapproveConfirmModal.eligibleCount}
            blockedCount={unapproveConfirmModal.blockedCount}
          />

          <TimesheetApproveConfirmModal
            isOpen={approveConfirmModal.open}
            onClose={handleCloseApproveConfirmModal}
            onConfirm={handleConfirmApproveModal}
            scheduleMismatch={approveConfirmModal.scheduleMismatch}
            details={approveConfirmModal.details}
          />

          {showDetailPanel && (
            <section ref={tabSectionRef} className="min-h-0">
              <TabSection
                handleStatusChange={handleStatusChange}
                requestApproveConfirmation={requestApproveConfirmation}
                setCheckboxes={setCheckboxes}
                timesheetsData={timesheetsData?.data}
                loadingStatus={loadingStatus}
                refresh={refresh}
                navigateToTabId={detailTabNavigateId}
                onTabNavigateConsumed={handleTabNavigateConsumed}
              />
            </section>
          )}
        </>
      )}
        </>
      )}
    </div>
  );
};

export default React.memo(TimesheetDetails);
